<template>
  <div
    id="app"
    class="ui"
  >
    <div class="header__placeholder"></div>
    <Header></Header>
    <div class="app-wrapper">
      <Sidebar class="scrollable"></Sidebar>
      <main class="main">
        <GlobalEvents
          @keydown.shift.tab="handleTabChange"
          @keydown.meta.111="toggleShortcuts"
        ></GlobalEvents>
        <router-view class="scrollable" />
      </main>
      <notifications class="scrollable"></notifications>
    </div>
    <Shortcuts v-show="shortcutsDisplay"></Shortcuts>
  </div>
</template>

<script>
import { mapState, mapActions } from 'vuex';
import GlobalEvents from 'vue-global-events';
import Header from './components/Header.vue';
import Sidebar from './components/Sidebar.vue';
import Notifications from './components/Notifications.vue';
import Shortcuts from './components/Shortcuts.vue';

import { EventBus } from '@/event-bus.js';

EventBus.$on('i-got-clicked', shiftTabCount => {
  console.log(`Oh, that's nice. It's gotten ${shiftTabCount} clicks! :)`);
});

export default {
  name: 'app',
  components: {
    Sidebar,
    Header,
    GlobalEvents,
    Shortcuts,
    Notifications,
  },
  computed: {
    ...mapState({
      sidebar: state => state.layout.sidebar,
    }),
  },
  data() {
    return {
      shiftTabCount: 0,
      shortcutsDisplay: false,
    };
  },
  mounted() {
    if (process.env.VUE_APP_TITLE) {
      document.title = process.env.VUE_APP_TITLE;
    }
  },
  methods: {
    ...mapActions({
      setSidebar: 'layout/sidebar',
    }),
    handleTabChange() {
      console.log(`**Shift+Tab**`);

      this.setSidebar(!this.sidebar);

      this.emitGlobalShiftTabEvent();
    },
    emitGlobalShiftTabEvent() {
      this.shiftTabCount++;
      // Send the event on a channel (i-got-clicked) with a payload (the click count.)
      EventBus.$emit('i-got-clicked', this.shiftTabCount);
    },
    toggleShortcuts() {
      this.shortcutsDisplay = !this.shortcutsDisplay;
    },
    enter(el, done) {
      console.log(this.$route.meta.animation, el, done);
      if (this.$route.meta.animation === 'special') {
        // apply special animation
      } else {
        // main page animation with a timeline using Greensock
      }
    },
    leave(el, done) {
      console.log(el, done);
      // main page animation with a timeline using Greensock
    },
  },
};
</script>

<style lang="scss">
* {
  box-sizing: border-box;
}

body {
  margin: 0;
  @include type;
  font-size: $base-font-size;
}

button,
input,
optgroup,
select,
textarea,
.btn {
  font-size: $base-font-size;
}

/**
 * Typography
 */
a {
  color: $link;
  text-decoration: none;
  &:hover,
  &.router-link-exact-active {
    color: $hover;
    text-decoration: underline;
  }
}

h1 {
  @include typeCondensed;
}

/**
 * General classes
 */
.scrollable {
  overflow-y: auto;
  overflow-x: hidden;
}

.text-center {
  text-align: center;
}

.text-right {
  text-align: right;
}

.text-left {
  text-align: left;
}

.float-right {
  float: right;
}

.float-left {
  float: left;
}

.align-items-start {
  align-items: flex-start;
}

.align-items-center {
  align-items: center;
}

.muted {
  opacity: 0.2;
}

.fill {
  display: flex;
  flex-direction: column;
}

.noselect {
  -webkit-touch-callout: none;
  -webkit-user-select: none;
  -khtml-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
  user-select: none;
}

.noclickable {
  pointer-events: none;
}

/**
 * Forms
 */
.form-control {
  border: 1px solid $border;
  padding: 5px 10px;
  display: block;
  height: 3em;
  width: 100%;
}

button,
input,
optgroup,
select,
textarea {
  font-family: inherit; /* 1 */
  line-height: 1.15; /* 1 */
  margin: 0; /* 2 */
}

/**
 * Show the overflow in IE.
 * 1. Show the overflow in Edge.
 */

button,
input {
  /* 1 */
  overflow: visible;
}

/**
 * Remove the inheritance of text transform in Edge, Firefox, and IE.
 * 1. Remove the inheritance of text transform in Firefox.
 */

button,
select {
  /* 1 */
  text-transform: none;
}

/**
 * Correct the inability to style clickable types in iOS and Safari.
 */

button,
[type='button'],
[type='reset'],
[type='submit'] {
  -webkit-appearance: button;
}

.btn {
  display: inline-block;
  padding: 0.5em 0.75em;
  border: 1px solid $border;
  cursor: pointer;
  height: 3em;
  outline: none;
  user-select: none;
  white-space: nowrap;

  .svg-inline--fa {
    margin-right: 5px;
  }
}

.btn-flex {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
}

.btn-default {
  color: $btn-default-color;
  background-color: $btn-default-bg;
  border-color: $btn-default-border;
  transition: background 0.2s linear;
  border-radius: $btn-border-radius;
  &:hover,
  &.active {
    color: $btn-default-hover-color;
    background-color: $btn-default-hover-bg;
    border-color: $btn-default-hover-border;
    text-decoration: none;
  }
}

.btn-grid {
  justify-content: flex-start;
  border-color: transparent;
}

.btn-success {
  color: $btn-success-color;
  background-color: $btn-success-bg;
  border-color: $btn-success-border;
  transition: background 0.2s linear;
  border-radius: $btn-border-radius;
  &:hover,
  &.active {
    color: $btn-success-hover-color;
    background-color: $btn-success-hover-bg;
    border-color: $btn-success-hover-border;
    text-decoration: none;
  }
}

.btn-muted {
  color: $btn-muted-color;
  background-color: $btn-muted-bg;
  border-color: $btn-muted-border;
  transition: background 0.2s linear;
  border-radius: $btn-border-radius;
  &:hover,
  &.active {
    color: $btn-muted-hover-color;
    background-color: $btn-muted-hover-bg;
    border-color: $btn-muted-hover-border;
    text-decoration: none;
  }
}

.btn-link {
  border-color: transparent;
}

.btn-group {
  display: flex;
  & > .btn {
    flex: 0 0 auto;
    text-align: center;
    border-radius: 0;
    border-right-width: 0;
    &:first-child {
      border-top-left-radius: $btn-border-radius;
      border-bottom-left-radius: $btn-border-radius;
    }
    &:last-child {
      border-top-right-radius: $btn-border-radius;
      border-bottom-right-radius: $btn-border-radius;
      border-right-width: 1px;
    }
  }
}

.disabled {
  pointer-events: none;
  opacity: 0.4;
}

/**
 * Layout
 */
.row {
  display: flex;
  margin-left: -1 * $gutter/2;
  margin-right: -1 * $gutter/2;
}

.col {
  flex: 1;
  padding-left: $gutter/2;
  padding-right: $gutter/2;
}

.container-fluid {
  padding-left: $gutter/2;
  padding-right: $gutter/2;
}

.main {
  display: flex;
  flex-direction: column;
  margin: 0 auto;
  & > * {
    flex: 1;
    &.row {
      max-height: 40px;
    }
  }
  & > .scrollable {
    padding: 30px $sidebar-width + $padding;
  }
}

.ui {
  height: 100vh;
  flex-direction: column;
  .header__placeholder {
    max-height: $header-height;
    min-height: $header-height;
    background-color: $inverse;
  }
}

.app-wrapper {
  display: flex;
  height: calc(100vh - 80px);
  & > * {
    flex: 1;
  }
}

.panel {
  border: 1px solid $muted;
  .panel__header,
  .tabs__header {
    border-bottom: 1px solid $muted;
  }
  .panel__body,
  .tabs__body {
    padding: $padding $padding * 2;
  }
}

.field {
  padding: $padding 0;
  .field__label {
    display: block;
    padding-bottom: $padding * 0.5;
  }

  .field__repeater {
    .field__repeater__item {
      display: flex;
      .form-control + .btn {
        border-left: 0;
      }
      .btn + .form-control {
        border-left: 0;
      }
    }
    .field__repeater__actions {
      text-align: right;
      padding-top: $padding;
    }
  }
}

$detail-header-height: 80px;
.index__header,
.detail__header {
  position: relative;
  padding-left: 20px;
  &.index__header {
    padding-left: 0;
  }
  height: $detail-header-height;
  h1 {
    margin: 0;
    line-height: $detail-header-height;
  }
  .btn-back {
    position: absolute;
    left: 0;
    top: 50%;
    transform: translateY(-50%);
  }
  .btn--save {
    position: absolute;
    right: 0;
    top: 50%;
    transform: translateY(-50%);
    padding: 0 6em;
    height: $detail-header-height * 0.6;
    line-height: $detail-header-height * 0.6;
    &.btn-loading {
      position: absolute;
      padding: 0 6em;
    }
  }
}

.nav--tabs {
  list-style-type: none;
  padding-left: 0;
  margin: 0;
  li {
    display: inline-block;
    a {
      display: block;
      padding: $padding $padding * 2;
    }
  }
}

.field--required {
  position: relative;
  &:after {
    content: '*';
    display: block;
    position: absolute;
    right: 20px;
    top: 60%;
    transform: translate(-50%, -50%);
  }
}

.has-error {
  .form-control {
    border-color: $fill-error;
  }
  .field--required {
    &:after {
      color: $fill-error;
    }
  }
}
</style>
